import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Checkbox, Label, TextLink, formatCount } from '@wit-support/ui';
import { Link } from '@tanstack/react-router';
import { activateLaunch, getLaunch, requestIdOf, reverifyLaunch, rollbackLaunch } from '../api.js';
import { adsLaunchStatusLabelRu } from '@wit-support/ui-domain';
import { launchErrorText } from '../media-errors.js';
import { displayName } from '../naming.js';
import { ADS_KEY, describeAdsError, isUpstreamCode } from '../shared.js';
import { SpendConfirmDialog, } from './spend-confirm-dialog.js';
import { spendTotalText } from './spend-summary.js';
import { agoText, OpenLaunchLink, waitingText } from './late-activation.js';
import { JOURNAL_ACTION_LABEL, JOURNAL_PATH, useJournalAvailable } from './journal-link.js';
/*
 * Прогоны по запускам пачки (спека «Конструктор FB v4» §8.3, §5.3, §11.4):
 * «Включить все сейчас…» и «Откатить неполные…». Нового маршрута нет — UI
 * по одному вызывает действия запусков; ошибка одного не останавливает
 * остальные; «Остановить» прекращает прогон после текущего вызова.
 */
const LAUNCH_FORMS = ['запуск', 'запуска', 'запусков'];
/** Запуск ждёт включения после прошедшего старта (сервер предложил «Включить сейчас»). */
export function isLateLaunch(l) {
    const a = l.availableActions ?? [];
    return a.includes('activate_late') || a.includes('force_activate_late');
}
/** Запуск создан не полностью: «Ошибка», а кампания в Meta есть (§5.3). */
export function isIncompleteLaunch(l) {
    return l.displayStatus === 'failed' && l.created.campaignId != null;
}
const EXCLUDED_REASON_RU = {
    'verify-errors': 'сверка нашла ошибки',
    busy: 'с запуском идёт другое действие',
};
/** Почему запуск не входит в массовое включение — словами (незнакомое — общей фразой). */
export function excludedReasonRu(reason) {
    return EXCLUDED_REASON_RU[reason] ?? 'сейчас его нельзя включить так';
}
/** Состав «Включить все сейчас…»: кого включаем и кто не входит (по виду пачки сервера). */
export function bulkLateSets(batch, launches) {
    const byId = new Map(launches.map((l) => [l.id, l]));
    const late = batch.lateStart;
    if (late) {
        return {
            activatable: late.activatable.flatMap((id) => (byId.has(id) ? [byId.get(id)] : [])),
            excluded: late.excluded.map((e) => ({
                id: e.launchId,
                launch: byId.get(e.launchId) ?? null,
                reason: e.reason,
            })),
        };
    }
    return {
        activatable: launches.filter((l) => (l.availableActions ?? []).includes('activate_late')),
        excluded: launches
            .filter((l) => !(l.availableActions ?? []).includes('activate_late') &&
            (l.availableActions ?? []).includes('force_activate_late'))
            .map((l) => ({ id: l.id, launch: l, reason: 'verify-errors' })),
    };
}
/**
 * Итог в день выбранных: всё выбранное — готовая сумма сервера пачки
 * (`lateStart.spendByCurrency`, `unknownSpendLaunches`); часть — сложение
 * готовых серверных сумм запусков по валютам (§3.11: суммы не умножаются).
 * Запуск без годной суммы в итог не входит и считается в `unknown`.
 */
export function bulkLateSpend(batch, all, selected) {
    const late = batch.lateStart;
    // серверная сумма — только если состав прогона тот же, что у сервера (все и только его запуски)
    if (late &&
        all.length > 0 &&
        all.length === late.activatable.length &&
        all.every((l) => selected.has(l.id))) {
        return {
            lines: Object.entries(late.spendByCurrency).map(([currency, totalMinor]) => ({
                currency,
                totalMinor,
            })),
            unknown: late.unknownSpendLaunches,
        };
    }
    const by = new Map();
    let unknown = 0;
    for (const l of all) {
        if (!selected.has(l.id))
            continue;
        const s = l.spend;
        // известна только при `usable === true` — то же правило, что у сервера (`late-start.ts`)
        // сумма строки может быть `null` (контракт #350: негодная строка) — тоже «неизвестна»
        if (s == null || s.usable !== true || s.totalMinor == null) {
            unknown += 1;
            continue;
        }
        by.set(s.currency, (by.get(s.currency) ?? 0) + s.totalMinor);
    }
    return {
        lines: [...by.entries()].map(([currency, totalMinor]) => ({ currency, totalMinor })),
        unknown,
    };
}
function launchLabel(l, id) {
    void id;
    return (l ? displayName(l.campaignName) : null) ?? 'Запуск без имени';
}
/**
 * Текст отказа строки прогона: фраза admin-api; голый код (незнакомый отказ
 * без фразы) людям не показываем — общая фраза.
 */
export function failureText(err) {
    const t = describeAdsError(err, 'write').replace(/\.$/, '');
    return /^[A-Za-z0-9_.:-]+$/.test(t) ? 'сервис рекламы отказал без пояснения' : t;
}
/** Причина отказа строки прогона — словами и, по месту, ссылкой. */
function useFailureReason() {
    const journal = useJournalAvailable();
    return (err, launchId) => {
        if (isUpstreamCode(err, 'verify-has-errors')) {
            return (_jsxs(_Fragment, { children: ["\u0441\u0432\u0435\u0440\u043A\u0430 \u043D\u0430\u0448\u043B\u0430 \u0440\u0430\u0441\u0445\u043E\u0436\u0434\u0435\u043D\u0438\u044F \u2014 ", _jsx(OpenLaunchLink, { id: launchId })] }));
        }
        if (isUpstreamCode(err, 'launch-busy'))
            return 'с запуском идёт другое действие';
        const requestId = journal ? requestIdOf(err) : null;
        const text = isUpstreamCode(err, 'meta-error') ? 'Meta отказала' : failureText(err);
        return requestId ? (_jsxs(_Fragment, { children: [text, " \u2014", ' ', _jsx(TextLink, { asChild: true, children: _jsx(Link, { to: JOURNAL_PATH, search: { requestId }, children: JOURNAL_ACTION_LABEL }) })] })) : (text);
    };
}
/** Опрос статуса после очереди: шаг и предел (тесты укорачивают). */
export const RUN_POLL = { intervalMs: 3000, maxMs: 10 * 60_000 };
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
/** Последовательный прогон с «Остановить»: состояние строк, запуск и опрос итогов из очереди. */
function useSequentialRun() {
    const [state, setState] = useState(null);
    const stopRef = useRef(false);
    // прогон уже идёт — второй не начинается (двойного вызова нет)
    const busyRef = useRef(false);
    // поколение состояния: сброс (новое открытие) или уход с экрана — старый опрос не пишет
    const genRef = useRef(0);
    const qc = useQueryClient();
    // экран ушёл посреди прогона — следующие вызовы (и включение после перепроверки) не уходят
    useEffect(() => () => {
        stopRef.current = true;
        genRef.current += 1;
    }, []);
    const run = async (rows, step) => {
        if (busyRef.current)
            return;
        busyRef.current = true;
        stopRef.current = false;
        const gen = genRef.current;
        let cur = rows.map((r) => ({ ...r, status: 'pending' }));
        const checks = new Map();
        const count = () => ({
            done: cur.filter((x) => x.status === 'ok').length,
            failed: cur.filter((x) => x.status === 'error').length,
            queued: checks.size,
        });
        const put = (running) => {
            if (genRef.current === gen)
                setState({ rows: cur, running, ...count() });
        };
        put(true);
        for (const [i, r] of rows.entries()) {
            if (stopRef.current) {
                cur = cur.map((x, j) => j >= i ? { ...x, status: 'error', reason: 'не выполнено — прогон остановлен' } : x);
                break;
            }
            const res = await step(r.id, () => stopRef.current);
            cur = cur.map((x, j) => {
                if (j !== i)
                    return x;
                if ('queued' in res)
                    return { ...x, status: 'pending', reason: res.reason };
                return res.ok ? { ...x, status: 'ok' } : { ...x, status: 'error', reason: res.reason };
            });
            if ('queued' in res)
                checks.set(r.id, res.check);
            put(true);
        }
        busyRef.current = false;
        put(false);
        void qc.invalidateQueries({ queryKey: ADS_KEY });
        // итоги из очереди: опрос, пока диалог тот же и экран открыт (закрыть можно —
        // статус виден на пачке)
        const started = Date.now();
        while (checks.size > 0 && genRef.current === gen && Date.now() - started < RUN_POLL.maxMs) {
            await sleep(RUN_POLL.intervalMs);
            if (genRef.current !== gen)
                return;
            for (const [id, check] of [...checks]) {
                const v = await check().catch(() => ({ status: 'pending' }));
                if (v.status === 'pending')
                    continue;
                checks.delete(id);
                cur = cur.map((x) => x.id !== id
                    ? x
                    : v.status === 'ok'
                        ? { ...x, status: 'ok', reason: undefined }
                        : { ...x, status: 'error', reason: v.reason });
            }
            put(false);
            void qc.invalidateQueries({ queryKey: ADS_KEY });
        }
        if (checks.size > 0 && genRef.current === gen) {
            cur = cur.map((x) => checks.has(x.id)
                ? { ...x, reason: 'ещё в очереди — итог будет виден на странице пачки' }
                : x);
            checks.clear();
            setState({
                rows: cur,
                running: false,
                done: cur.filter((x) => x.status === 'ok').length,
                failed: cur.filter((x) => x.status === 'error').length,
                queued: cur.filter((x) => x.status === 'pending').length,
            });
        }
    };
    return {
        state,
        run,
        stop: () => {
            stopRef.current = true;
        },
        reset: () => {
            if (busyRef.current)
                return;
            genRef.current += 1;
            setState(null);
        },
    };
}
/**
 * Заголовок итога: подтверждённое сервером отдельно от принятого в очередь —
 * «Откачено 1 из 3 · в очереди на откат: 2», «Включено 2 из 4».
 */
export function runTitle(st, doneWord, queuedWord) {
    const base = `${doneWord} ${st.done} из ${st.rows.length}`;
    return st.queued > 0 ? `${base} · ${queuedWord}: ${st.queued}` : base;
}
/** Терминальные «включён» — показы идут или ждут своего старта. */
const ACTIVE_STATUSES = new Set(['running', 'waiting_start']);
/**
 * Включение по ответу сервера (Codex): «✓ включён» — только когда статус
 * запуска это подтверждает; «включается» — ждём; статус сменился на другой —
 * отказ с этим статусом словами.
 */
export function activationVerdict(l, beforeUpdatedAt) {
    if (ACTIVE_STATUSES.has(l.displayStatus))
        return { status: 'ok' };
    if (l.displayStatus === 'activating')
        return { status: 'pending', reason: 'включается…' };
    if (l.displayStatus === 'ready_paused' && l.updatedAt === beforeUpdatedAt) {
        return { status: 'pending', reason: 'поставлено в очередь' };
    }
    return {
        status: 'error',
        reason: `не включён — статус «${adsLaunchStatusLabelRu(l.displayStatus, l.activation?.reason).toLowerCase()}»`,
    };
}
/**
 * Откат по статусу САМОГО отката (ревью SB-5 P1-3): «✓ откачен» — только
 * `rolled_back`; «не удался» — только когда откат начался (`rolling_back` —
 * в опросе или в истории состояний после запроса) и запуск вышел из него не в
 * `rolled_back`. Постановка в очередь меняет `updatedAt`, но не состояние —
 * это ещё «в очереди», а не отказ.
 */
export function rollbackVerdict(l, watch) {
    if (l.displayStatus === 'rolled_back')
        return { status: 'ok' };
    if (l.displayStatus === 'rolling_back') {
        watch.started = true;
        return { status: 'pending', reason: 'откатывается…' };
    }
    const fresh = watch.historyBefore != null ? l.stateHistory.slice(watch.historyBefore) : [];
    if (fresh.some((h) => h.to === 'rolling_back'))
        watch.started = true;
    if (!watch.started)
        return { status: 'pending', reason: 'поставлено в очередь' };
    const failedStep = [...(l.steps ?? [])].reverse().find((st) => st.error != null);
    const why = failedStep?.error != null ? launchErrorText(failedStep.error).text : null;
    return {
        status: 'error',
        reason: why ? `откат не удался: ${why.replace(/\.$/, '')}` : 'откат не удался',
    };
}
/**
 * Снимок на время открытого диалога (P1 ревью SB-5): состав прогона
 * фиксируется при открытии и не меняется от опроса пачки — иначе итог по
 * каждому запуску пропадал, а кнопка запуска возвращалась посреди прогона.
 * Обновляется только при следующем открытии.
 */
function useOpenSnapshot(open, value, onOpen) {
    const [snap, setSnap] = useState(value);
    const wasOpen = useRef(false);
    useEffect(() => {
        if (open && !wasOpen.current) {
            setSnap(value);
            onOpen(value);
        }
        wasOpen.current = open;
        // только переход «закрыт → открыт»
        // (value/onOpen намеренно не в зависимостях: опрос не должен менять открытый диалог)
    }, [open]);
    return open ? snap : value;
}
/**
 * «Включить все сейчас…» (§8.3, §11.4): строки с галочками (сумма в день
 * каждого — сервера), «Не входят (включайте по одному)», итог только по
 * отмеченным, галочка «Понимаю…», прогон по одному с «Остановить» и итог
 * «✓ включён / ⛔ причина» по каждому.
 */
export function BulkLateActivateDialog({ open, onOpenChange, batch, launches, accountName, onRunningChange, }) {
    const runner = useSequentialRun();
    const reasonOf = useFailureReason();
    const [selected, setSelected] = useState(() => new Set());
    const snap = useOpenSnapshot(open, { batch, launches }, (v) => {
        runner.reset();
        setSelected(new Set(bulkLateSets(v.batch, v.launches).activatable.map((l) => l.id)));
    });
    const { activatable, excluded } = bulkLateSets(snap.batch, snap.launches);
    const running = runner.state?.running === true;
    useEffect(() => onRunningChange?.(running), [running, onRunningChange]);
    const { lines, unknown } = bulkLateSpend(snap.batch, activatable, selected);
    const chosen = activatable.filter((l) => selected.has(l.id));
    const n = chosen.length;
    const amount = unknown === 0 && lines.length > 0 ? lines.map((x) => spendTotalText(x)).join(' + ') : null;
    /** Прогон остановлен между перепроверкой и включением — включения нет (Codex P2). */
    const STOPPED = { ok: false, reason: 'не включён — прогон остановлен' };
    const step = async (id, stopped) => {
        const l = activatable.find((x) => x.id === id);
        const before = l?.updatedAt ?? null;
        let activated = null;
        try {
            if (l?.lateStart?.verifyFresh === false) {
                const r = await reverifyLaunch(id);
                if (r.verify?.hasErrors === true) {
                    return {
                        ok: false,
                        reason: (_jsxs(_Fragment, { children: ["\u0441\u0432\u0435\u0440\u043A\u0430 \u043D\u0430\u0448\u043B\u0430 \u0440\u0430\u0441\u0445\u043E\u0436\u0434\u0435\u043D\u0438\u044F \u2014 ", _jsx(OpenLaunchLink, { id: id })] })),
                    };
                }
            }
            try {
                if (stopped())
                    return STOPPED;
                activated = await activateLaunch(id, { acknowledgeLateStart: true });
            }
            catch (err) {
                // сверка устарела между видом пачки и вызовом — перепроверить и повторить один раз
                if (!isUpstreamCode(err, 'reverify-required'))
                    throw err;
                const r = await reverifyLaunch(id);
                if (r.verify?.hasErrors === true) {
                    return {
                        ok: false,
                        reason: (_jsxs(_Fragment, { children: ["\u0441\u0432\u0435\u0440\u043A\u0430 \u043D\u0430\u0448\u043B\u0430 \u0440\u0430\u0441\u0445\u043E\u0436\u0434\u0435\u043D\u0438\u044F \u2014 ", _jsx(OpenLaunchLink, { id: id })] })),
                    };
                }
                if (stopped())
                    return STOPPED;
                activated = await activateLaunch(id, { acknowledgeLateStart: true });
            }
            // «✓ включён» — только по статусу, который вернул сервер
            const v = activationVerdict(activated, before);
            if (v.status === 'ok')
                return { ok: true };
            if (v.status === 'error')
                return { ok: false, reason: v.reason };
            return {
                queued: true,
                reason: v.reason ?? 'включается…',
                check: async () => activationVerdict(await getLaunch(id), before),
            };
        }
        catch (err) {
            return { ok: false, reason: reasonOf(err, id) };
        }
    };
    const progress = runner.state
        ? {
            rows: runner.state.rows,
            running: runner.state.running,
            statusText: `Включено ${runner.state.done} из ${runner.state.rows.length}…`,
            onStop: runner.stop,
            stopHint: '«Остановить» прекратит прогон после текущего запуска. Уже включённые продолжат работать — их это не выключает.',
            doneTitle: runTitle(runner.state, 'Включено', 'включаются'),
        }
        : null;
    return (_jsx(SpendConfirmDialog, { open: open, onOpenChange: onOpenChange, title: `Включить ${formatCount(n, LAUNCH_FORMS)} сейчас?`, lines: progress ? [] : lines, unknownLaunches: progress ? 0 : unknown, rows: progress ? [] : [{ label: 'Кампаний', value: String(n) }], confirmLabel: `Включить ${n}`, acknowledgeLabel: amount
            ? `Понимаю: расход около ${amount} в день начнётся сразу`
            : 'Понимаю: расход начнётся сразу', blockedReason: n === 0 ? 'Отметьте хотя бы один запуск' : null, progress: progress, onConfirm: () => runner.run(chosen.map((l) => ({ id: l.id, label: launchLabel(l, l.id), status: 'pending' })), step), children: progress ? null : (_jsxs("div", { className: "flex flex-col gap-3", "data-testid": "bulk-late-body", children: [_jsx("ul", { className: "flex flex-col gap-2", "aria-label": "\u0417\u0430\u043F\u0443\u0441\u043A\u0438, \u043A\u043E\u0442\u043E\u0440\u044B\u0435 \u0431\u0443\u0434\u0443\u0442 \u0432\u043A\u043B\u044E\u0447\u0435\u043D\u044B", children: activatable.map((l) => {
                        const id = `bulk-late-${l.id}`;
                        const late = l.lateStart;
                        return (_jsxs("li", { className: "flex items-start gap-2", children: [_jsx(Checkbox, { id: id, checked: selected.has(l.id), onCheckedChange: (v) => setSelected((cur) => {
                                        const next = new Set(cur);
                                        if (v === true)
                                            next.add(l.id);
                                        else
                                            next.delete(l.id);
                                        return next;
                                    }) }), _jsxs(Label, { htmlFor: id, className: "min-w-0 [overflow-wrap:anywhere]", children: [launchLabel(l, l.id), " \u00B7 ", accountName(l), " \u00B7", ' ', l.spend && l.spend.usable !== false
                                            ? `${spendTotalText(l.spend)}/день`
                                            : 'сумма неизвестна', late
                                            ? late.waitingOver24h
                                                ? ` · ⚠ ждёт ${waitingText(late.lateByMinutes)}`
                                                : ` · старт был ${agoText(late.lateByMinutes)}`
                                            : ''] })] }, l.id));
                    }) }), excluded.length > 0 ? (_jsxs("p", { "data-testid": "bulk-late-excluded", className: "[overflow-wrap:anywhere]", children: ["\u041D\u0435 \u0432\u0445\u043E\u0434\u044F\u0442 (\u0432\u043A\u043B\u044E\u0447\u0430\u0439\u0442\u0435 \u043F\u043E \u043E\u0434\u043D\u043E\u043C\u0443):", ' ', excluded.map((e, i) => (_jsxs("span", { children: [i > 0 ? '; ' : '', launchLabel(e.launch, e.id), " \u2014 ", excludedReasonRu(e.reason)] }, e.id)))] })) : null, _jsx("p", { className: "text-[var(--color-text-secondary)]", children: "\u0417\u0430\u043F\u0443\u0441\u043A\u0438 \u0432\u043A\u043B\u044E\u0447\u0430\u044E\u0442\u0441\u044F \u043F\u043E \u043E\u0434\u043D\u043E\u043C\u0443; \u043E\u0448\u0438\u0431\u043A\u0430 \u043E\u0434\u043D\u043E\u0433\u043E \u043D\u0435 \u043E\u0441\u0442\u0430\u043D\u0430\u0432\u043B\u0438\u0432\u0430\u0435\u0442 \u043E\u0441\u0442\u0430\u043B\u044C\u043D\u044B\u0435. \u0415\u0441\u043B\u0438 \u0437\u0430\u043A\u0440\u044B\u0442\u044C \u0432\u043A\u043B\u0430\u0434\u043A\u0443, \u0443\u0436\u0435 \u0432\u043A\u043B\u044E\u0447\u0451\u043D\u043D\u044B\u0435 \u043F\u0440\u043E\u0434\u043E\u043B\u0436\u0430\u0442 \u0442\u0440\u0430\u0442\u0438\u0442\u044C, \u043E\u0441\u0442\u0430\u043B\u044C\u043D\u044B\u0435 \u043E\u0441\u0442\u0430\u043D\u0443\u0442\u0441\u044F \u043D\u0435 \u0432\u043A\u043B\u044E\u0447\u0451\u043D\u043D\u044B\u043C\u0438." })] })) }));
}
/**
 * «Откатить неполные…» (§5.3, §11.4): удалить из Meta созданное у запусков,
 * созданных не полностью, — по одному, с прогрессом и итогом. Включённое
 * сервер откатывать не даёт (`rollback-forbidden-active`) — это строка итога.
 */
export function IncompleteRollbackDialog({ open, onOpenChange, launches: launchesNow, }) {
    const runner = useSequentialRun();
    const reasonOf = useFailureReason();
    const launches = useOpenSnapshot(open, launchesNow, () => runner.reset());
    const adSets = launches.reduce((s, l) => s + Object.keys(l.created.adSetIds ?? {}).length, 0);
    const progress = runner.state
        ? {
            rows: runner.state.rows,
            running: runner.state.running,
            statusText: `Отправлено на откат: ${runner.state.done + runner.state.queued} из ${runner.state.rows.length}…`,
            onStop: runner.stop,
            stopHint: '«Остановить» прекратит прогон после текущего запуска. Уже отправленное на откат не вернётся.',
            doneTitle: runTitle(runner.state, 'Откачено', 'в очереди на откат'),
        }
        : null;
    return (_jsx(SpendConfirmDialog, { open: open, onOpenChange: onOpenChange, title: "\u041E\u0442\u043A\u0430\u0442\u0438\u0442\u044C \u043D\u0435\u043F\u043E\u043B\u043D\u044B\u0435 \u0437\u0430\u043F\u0443\u0441\u043A\u0438?", lines: [], rows: [
            { label: 'Кампаний', value: String(launches.length) },
            { label: 'Групп объявлений', value: String(adSets) },
        ], confirmLabel: "\u041E\u0442\u043A\u0430\u0442\u0438\u0442\u044C", confirmVariant: "danger", blockedReason: launches.length === 0 ? 'Откатывать нечего' : null, progress: progress, onConfirm: () => runner.run(launches.map((l) => ({ id: l.id, label: launchLabel(l, l.id), status: 'pending' })), async (id) => {
            try {
                // история состояний ДО запроса — чтобы отличить «откат начался» от прошлых
                const watch = { historyBefore: null, started: false };
                try {
                    watch.historyBefore = (await getLaunch(id)).stateHistory.length;
                }
                catch {
                    // не прочиталось — судим только по увиденному `rolling_back`
                }
                // 202 `jobId` — откат только поставлен в очередь: итог — по статусу отката
                await rollbackLaunch(id, {});
                return {
                    queued: true,
                    reason: 'поставлено в очередь',
                    check: async () => rollbackVerdict(await getLaunch(id), watch),
                };
            }
            catch (err) {
                return {
                    ok: false,
                    reason: isUpstreamCode(err, 'rollback-forbidden-active')
                        ? 'в Meta что-то включено — сначала поставьте запуск на паузу'
                        : reasonOf(err, id),
                };
            }
        }), children: progress ? null : (_jsxs("p", { "data-testid": "incomplete-rollback-text", children: ["\u0411\u0443\u0434\u0443\u0442 \u0443\u0434\u0430\u043B\u0435\u043D\u044B \u0438\u0437 Meta:", ' ', formatCount(launches.length, ['кампания', 'кампании', 'кампаний']), ",", ' ', formatCount(adSets, ['группа объявлений', 'группы объявлений', 'групп объявлений']), ". \u041E\u0442\u043C\u0435\u043D\u0438\u0442\u044C \u043D\u0435\u043B\u044C\u0437\u044F. \u0417\u0430\u043F\u0443\u0441\u043A\u0438 \u043E\u0442\u043A\u0430\u0442\u044B\u0432\u0430\u044E\u0442\u0441\u044F \u043F\u043E \u043E\u0434\u043D\u043E\u043C\u0443; \u043E\u0448\u0438\u0431\u043A\u0430 \u043E\u0434\u043D\u043E\u0433\u043E \u043D\u0435 \u043E\u0441\u0442\u0430\u043D\u0430\u0432\u043B\u0438\u0432\u0430\u0435\u0442 \u043E\u0441\u0442\u0430\u043B\u044C\u043D\u044B\u0435."] })) }));
}
//# sourceMappingURL=batch-runs.js.map